import React, { useMemo } from "react";
import { isFunction, isNil, isUndefined } from "lodash";
import PropsOf from "uikit/src/types/PropsOf";
import styled from "styled-components";
import { theme } from "uikit";

import { useKeyBindSettings } from "../../../../../../../../../../hooks/useKeyBindSettings";
import { BaseKeybindEntries } from "../../../../../../../../../../redux/reducers/settings/keybinds";
import { useTypedSelector } from "../../../../../../../../../../redux/store";
import keyNames from "../../../../../../../../../../utils/keyNames";

const Root = styled.button<Button.Root>`
	all: unset;

	position: relative;

	display: flex;

	align-items: center;
	justify-content: center;

	width: 28px;
	height: 28px;

	border-radius: 5px;

	background: ${theme.colors.white};

	cursor: pointer;

	overflow: hidden;

	--icon-color-1: ${({ active = false }) =>
		active ? theme.colors.accent : theme.colors.primary};

	&:hover {
		&::before {
			content: "";

			position: absolute;

			width: 100%;
			height: 100%;

			background: rgba(0, 0, 0, 0.03);
		}
	}

	&:disabled {
		background: #f5f5f5;

		--icon-color-1: #b5c0c7;
	}
`;

const Button: React.FC<Button.Props> = ({ settingsKeyId, ...props }) => {
	useKeyBindSettings(settingsKeyId, (event) => {
		if (!props.disabled) props.onClick?.();

		event.preventDefault();
		event.stopPropagation();
	});

	const keys = useTypedSelector((state) =>
		isNil(settingsKeyId)
			? []
			: state?.settings?.keybinds?.[settingsKeyId]?.map(
					(key) => keyNames[key],
			  ) || [],
	);

	const keysString = useMemo(
		() => (keys.length ? `(${keys.join("+")})` : ""),
		[keys],
	);

	const title = useMemo(
		() => {
			if (isFunction(props.title)) return props.title(keys);

			if (isUndefined(props.title)) return undefined;

			return `${props.title}${
				!isUndefined(props.title) ? " " : ""
			}${keysString}`;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[keys, keysString, props.title],
	);

	return <Root {...props} title={title} />;
};

declare namespace Button {
	interface Props extends Omit<PropsOf<typeof Root>, "title" | "onClick"> {
		settingsKeyId?: keyof BaseKeybindEntries<"meta">;

		title?: ((keys: string[]) => string) | string;

		onClick?: VoidFunction;
	}

	interface Root {
		active?: boolean;
	}
}

export default Button;
